import mixpanel, { Dict } from 'mixpanel-browser';

import { ALL_ATTRIBUTION_KEYS } from '@/shared/constants/attribution';
import {
  IS_LOCAL_BUILD,
  IS_MIXPANEL_DEV_MODE_ENABLED,
  IS_PRODUCTION_BUILD,
  IS_WHITE_LABEL,
} from '@/shared/environments';
import {
  AttributionParams,
  captureAttributionParamsFromUrl,
  getGA4ClientId,
  getStoredAttributionParams,
} from '@/shared/lib/attribution';
import { getDeviceType } from '@/shared/lib/browser';
import { isTrackingDisabled } from '@/shared/lib/isTrackingDisabled';
import { mirrorTrialFunnelEvent } from '@/shared/thirdParty/gtm';
import { User } from '@/shared/types/User';

import { IS_MIXPANEL_DEBUGGER_ENABLED } from './constants';
import { createMixpanelConfig } from './mixpanelConfig';
import { ProjectData, useMixpanelPropsStore } from './useMixpanelPropsStore';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;
const MIXPANEL_PROXY_URL = import.meta.env.VITE_MIXPANEL_PROXY_URL;

// TODO move to abstraction slice [hash234123]
const MIXPANEL_ENABLED =
  !!MIXPANEL_TOKEN && (IS_PRODUCTION_BUILD || IS_MIXPANEL_DEV_MODE_ENABLED) && !isTrackingDisabled();

const deviceType = getDeviceType();
let currentInterfaceLanguage = 'en';
let currentBrowserLanguage = '';

const getLanguageProperties = () => ({
  browserLanguage: currentBrowserLanguage,
  interfaceLanguage: currentInterfaceLanguage,
});

/**
 * Register attribution params as Mixpanel super properties.
 * - register() = last-touch (overwritten on each visit with UTMs)
 * - register_once() = first-touch (set only once, never overwritten)
 */
const registerAttributionSuperProperties = (params: AttributionParams) => {
  if (!MIXPANEL_ENABLED) return;

  // Last-touch: always overwrite with current values
  const lastTouch: Record<string, string> = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    if (params[key]) {
      lastTouch[key] = params[key]!;
    }
  }

  if (Object.keys(lastTouch).length > 0) {
    mixpanel.register(lastTouch);
  }

  // First-touch: set only once (never overwritten)
  const firstTouch: Record<string, string> = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    if (params[key]) {
      firstTouch[`first_${key}`] = params[key]!;
    }
  }

  if (Object.keys(firstTouch).length > 0) {
    mixpanel.register_once(firstTouch);
  }
};

/**
 * Register GA4 client_id as super property + user property for cross-system joins.
 */
let ga4ClientIdRegistered = false;
let ga4RetryTimer: null | ReturnType<typeof setTimeout> = null;

const registerGA4ClientId = () => {
  if (!MIXPANEL_ENABLED || ga4ClientIdRegistered) return;

  const clientId = getGA4ClientId();
  if (clientId) {
    mixpanel.register({ ga4_client_id: clientId });
    mixpanel.people.set({ ga4_client_id: clientId });
    ga4ClientIdRegistered = true;

    // Cancel pending retry if it exists
    if (ga4RetryTimer) {
      clearTimeout(ga4RetryTimer);
      ga4RetryTimer = null;
    }
  }
};

// --- Init ---
export const initMixpanel = (interfaceLanguage = 'en', browserLanguage = '') => {
  currentInterfaceLanguage = interfaceLanguage;
  currentBrowserLanguage = browserLanguage;

  if (MIXPANEL_ENABLED) {
    mixpanel.init(
      MIXPANEL_TOKEN || '',
      createMixpanelConfig({
        isWhiteLabel: IS_WHITE_LABEL,
        proxyUrl: MIXPANEL_PROXY_URL,
      }),
    );
    mixpanel.register({ deviceType, ...getLanguageProperties() });

    // Capture UTM/gclid from URL on every page load
    const freshParams = captureAttributionParamsFromUrl();

    // Use fresh URL params if present, otherwise fall back to stored
    const attributionParams = Object.keys(freshParams).length > 0 ? freshParams : getStoredAttributionParams();

    registerAttributionSuperProperties(attributionParams);

    // GA4 client_id — may not be available immediately (GTM loads async)
    registerGA4ClientId();
    if (!ga4ClientIdRegistered) {
      ga4RetryTimer = setTimeout(registerGA4ClientId, 2000);
    }
  }
};

export const setMixpanelUser = (id: User['id'], email: User['email'], emailType: User['email_type']) => {
  if (MIXPANEL_ENABLED) {
    const mixpanelEmail: unknown = mixpanel.get_property('email');

    mixpanel.identify(id);

    mixpanel.people.set({
      ...getLanguageProperties(),
      ...(!mixpanelEmail || mixpanelEmail !== email ? { $email: email, emailType } : {}),
    });

    mixpanel.register({ email, emailType, ...getLanguageProperties() });

    const stored = getStoredAttributionParams();
    if (Object.keys(stored).length > 0) {
      const profileFirstTouch: Record<string, string> = {};
      for (const key of ALL_ATTRIBUTION_KEYS) {
        if (stored[key]) {
          profileFirstTouch[`first_${key}`] = stored[key]!;
        }
      }
      if (Object.keys(profileFirstTouch).length > 0) {
        mixpanel.people.set_once(profileFirstTouch);
      }
    }

    registerGA4ClientId();
  }
};

export const setMixpanelInterfaceLanguage = (interfaceLanguage: string, browserLanguage: string) => {
  currentInterfaceLanguage = interfaceLanguage;
  currentBrowserLanguage = browserLanguage;

  if (!MIXPANEL_ENABLED) return;

  const languageProperties = getLanguageProperties();
  mixpanel.register(languageProperties);
  mixpanel.people.set(languageProperties);
};

export const setMixpanelSubscriptionProfile = (data: {
  hasActiveSubscription: boolean;
  hasTrialPlan: boolean;
  planInterval?: string;
  planName?: string;
}) => {
  if (!MIXPANEL_ENABLED) return;
  mixpanel.people.set({
    has_active_subscription: data.hasActiveSubscription,
    has_trial_plan: data.hasTrialPlan,
    ...(data.planName && { plan_name: data.planName }),
    ...(data.planInterval && { plan_interval: data.planInterval }),
  });
};

export const shutdownMixpanel = () => {
  if (MIXPANEL_ENABLED) {
    mixpanel.reset();
    mixpanel.register({ deviceType, ...getLanguageProperties() });
    const stored = getStoredAttributionParams();
    registerAttributionSuperProperties(stored);
    registerGA4ClientId();
  }
};

type EventPayload = {
  metadata?: {
    shouldSendRaskTTSData?: boolean;
    shouldSendSubscriptionData?: boolean;
  };
  eventName: string;
  properties?: object;
};

export const trackEvent = ({ eventName, metadata, properties }: EventPayload) => {
  const { projectData, subscriptionData } = useMixpanelPropsStore.getState();
  const mutableProjectData: Partial<ProjectData> = { ...projectData };

  if (mutableProjectData?.raskTTS && !metadata?.shouldSendRaskTTSData) {
    delete mutableProjectData.raskTTS;
  }

  const eventProperties = { ...subscriptionData, ...mutableProjectData, ...properties };

  if (IS_LOCAL_BUILD) {
    console.info('%c[Mixpanel] %s', 'color: #7F56D9; font-weight: bold', eventName, eventProperties);
  }

  if (MIXPANEL_ENABLED) {
    mixpanel.track(eventName, eventProperties);

    if (IS_MIXPANEL_DEBUGGER_ENABLED) {
      const emailType = mixpanel.get_property('emailType') as string;
      console.info('%cMixpanel debugger event:', 'color: #7F56D9');
      console.table({ deviceType, emailType, eventName, ...eventProperties });
    }
  }

  mirrorTrialFunnelEvent(eventName, subscriptionData?.hasTrialPlan, properties);
};

export const setMixpanelUserProperty = (userId: string, prop: Dict) => {
  if (!MIXPANEL_ENABLED) {
    return;
  }

  mixpanel.identify(userId);
  mixpanel.people.set(prop);
};

export const setMixpanelUserPropertiesOnce = (properties: Dict) => {
  if (!MIXPANEL_ENABLED || !Object.keys(properties).length) return;
  mixpanel.people.set_once(properties);
};
